import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { FiClipboard } from "react-icons/fi";
import { getHistory } from "./History.api";
import { Participant } from "./History.interface";
import { useAuth } from "context/auth-context";
import { dateFormatHandler, DD_MMM_YYYY } from "./../../lib/date-formatter-moment";
import PaginationNew from "components/Pagination/PaginationNew";
import { Badge, EmptyState, SkeletonRows, Tone } from "components/ui";

export const History = () => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const getHistoryData = useQuery(["history", currentPage], () => getHistory(auth.panelistId, currentPage), {
    refetchOnWindowFocus: false,
  });

  if (getHistoryData.isLoading) {
    return (
      <div>
        <span className="sr-only">{t("page.referral.Loading")}</span>
        <SkeletonRows rows={6} />
      </div>
    );
  }

  const historyList = getHistoryData.data?.result?.historyList;

  const getStatuses = (survey: Participant) => {
    const statuses: { label: string; tone: Tone }[] = [];
    if (survey?.status === "Complete") {
      statuses.push({ label: t("home.about.Completed"), tone: "success" });
    }
    if (survey?.status === "Terminate") {
      statuses.push({ label: t("page.history.Terminated"), tone: "danger" });
    }
    if (survey?.status === "OverQuota" || survey?.status === "quotafull") {
      statuses.push({ label: t("page.history.quotafull"), tone: "warning" });
    }
    if (survey?.status === "Security" || survey?.participantsRedirectUrl?.includes("security")) {
      statuses.push({ label: t("page.history.securityTerminated"), tone: "danger" });
    }
    if (statuses.length === 0) {
      statuses.push({ label: t("page.history.notfound"), tone: "neutral" });
    }
    return statuses;
  };

  return (
    <>
      <div className="zs-table-wrap is-flush">
        <table className="zs-table">
          <thead>
            <tr>
              <th scope="col" className="w-16">
                {t("pages.referral.SrNo")}
              </th>
              <th scope="col">{t("page.history.SurveyName")}</th>
              <th scope="col" className="is-num">
                {t("page.history.Points")}
              </th>
              <th scope="col">{t("page.history.Status")}</th>
              <th scope="col">{t("page.history.AttemptDate")}</th>
            </tr>
          </thead>
          <tbody data-testid="survey1">
            {historyList?.length > 0 &&
              historyList.map((survey: Participant, i: number) => {
                const serialNumber = (currentPage - 1) * 10 + i + 1;
                const statuses = getStatuses(survey);
                const mainStatus = statuses[0];
                const remainingCount = statuses.length - 1;
                return (
                  <tr key={i}>
                    <td className="tabular-nums text-ink-3">{serialNumber}</td>
                    <td className="is-strong max-w-[320px] truncate" title={survey.surveyName}>
                      {survey.surveyName}
                    </td>
                    <td className="is-num is-strong">{Math.floor(+survey.points)}</td>
                    <td>
                      <div className="relative inline-block group">
                        <Badge tone={mainStatus.tone} dot>
                          {mainStatus.label}
                          {remainingCount > 0 && ` +${remainingCount}`}
                        </Badge>
                        {remainingCount > 0 && (
                          <div className="absolute z-10 hidden p-2 mb-2 -translate-x-1/2 left-1/2 bottom-full group-hover:block zs-popover w-max">
                            <div className="flex flex-col gap-1">
                              {statuses.map((s, idx) => (
                                <Badge key={idx} tone={s.tone}>
                                  {s.label}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-ink-3">{dateFormatHandler(survey?.createdAt, DD_MMM_YYYY)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {historyList && historyList.length > 0 ? (
        <div className="zs-table-footer">
          <span>
            Page {currentPage} of {getHistoryData?.data?.result?.totalPages}
          </span>
          <PaginationNew
            setCurrentPage={setCurrentPage}
            totalPages={getHistoryData?.data?.result?.totalPages}
            currentPage={currentPage}
          />
        </div>
      ) : (
        <EmptyState icon={<FiClipboard />} title={t("page.mysurvey.nodata")} />
      )}
    </>
  );
};
